import { useEffect, useMemo, useRef, useState } from "react";
import { Crosshair, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { PlacesAutocompleteInput, PlaceSelection } from "@/components/maps/PlacesAutocompleteInput";
import { loadGoogleMaps } from "@/lib/google-maps";

type LatLng = { lat: number; lng: number };

function extractCityCountry(place: any): { city?: string; country?: string } {
  const comps: any[] = place?.address_components || [];
  const find = (type: string) => comps.find((c) => (c.types || []).includes(type))?.long_name;
  const city =
    find("locality") ||
    find("postal_town") ||
    find("administrative_area_level_2") ||
    find("administrative_area_level_1");
  const country = find("country");
  return { city, country };
}

export function MapPicker({
  address,
  onAddressChange,
  onPlaceSelected,
  lat,
  lng,
  onLatLngChange,
  disabled,
}: {
  address: string;
  onAddressChange: (v: string) => void;
  onPlaceSelected: (p: PlaceSelection) => void;
  lat: number | null;
  lng: number | null;
  onLatLngChange: (v: LatLng | null) => void;
  disabled?: boolean;
}) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);

  const center = useMemo<LatLng>(() => {
    // default to Accra if unset
    return {
      lat: typeof lat === "number" ? lat : 5.6037,
      lng: typeof lng === "number" ? lng : -0.187,
    };
  }, [lat, lng]);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps({ libraries: ["places", "marker"] })
      .then(() => {
        if (!mounted) return;
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setLoading(false);
        setMapError(err instanceof Error ? err.message : "Failed to load Google Maps");
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (loading || mapError) return;
    if (!mapDivRef.current) return;
    const google = (window as any).google;
    if (!google?.maps || typeof google.maps.Map !== "function") {
      setMapError("Google Maps is not available");
      return;
    }

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapDivRef.current, {
        center,
        zoom: 15,
        clickableIcons: false,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
      });
      markerRef.current = new google.maps.Marker({
        position: center,
        map: mapRef.current,
        draggable: true,
      });
      geocoderRef.current = new google.maps.Geocoder();

      // Click to set marker
      mapRef.current.addListener("click", (e: any) => {
        if (disabled) return;
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        markerRef.current?.setPosition(pos);
        onLatLngChange(pos);
        reverseGeocode(pos);
      });

      // Drag marker
      markerRef.current.addListener("dragend", (e: any) => {
        if (disabled) return;
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        onLatLngChange(pos);
        reverseGeocode(pos);
      });
    } else {
      mapRef.current.setCenter(center);
      markerRef.current?.setPosition(center);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, mapError]);

  // Keep marker in sync if lat/lng changes
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    mapRef.current.setCenter(center);
    markerRef.current.setPosition(center);
  }, [center]);

  const reverseGeocode = (pos: LatLng) => {
    const geocoder = geocoderRef.current;
    if (!geocoder) return;
    geocoder.geocode({ location: pos }, (results: any, status: string) => {
      if (status !== "OK" || !results?.length) return;
      const formatted = results[0]?.formatted_address;
      const { city, country } = extractCityCountry(results[0]);
      if (formatted) {
        onAddressChange(formatted);
        onPlaceSelected({
          formattedAddress: formatted,
          lat: pos.lat,
          lng: pos.lng,
          placeId: results[0]?.place_id,
          city,
          country,
        });
      }
    });
  };

  const useCurrentLocation = async () => {
    if (disabled) return;
    if (!navigator.geolocation) {
      setMapError("Geolocation not supported");
      return;
    }
    setMapError(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        onLatLngChange(pos);
        reverseGeocode(pos);
      },
      (err) => {
        setMapError(err.message || "Failed to get current location");
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <PlacesAutocompleteInput
            value={address}
            onValueChange={onAddressChange}
            onPlaceSelected={onPlaceSelected}
            placeholder="Type your business address"
            disabled={disabled}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-10 px-3"
          onClick={useCurrentLocation}
          disabled={disabled || loading}
          title="Use my current location"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
        </Button>
      </div>

      {mapError ? (
        <div className="rounded-lg border border-border bg-accent/40 p-3 text-sm text-muted-foreground">
          Map unavailable ({mapError}). You can still type the address above.
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <div ref={mapDivRef} className="h-[260px] w-full bg-muted" />
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Tip: click on the map or drag the pin to fine‑tune the location.
      </p>
    </div>
  );
}


